import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/filter';
import { APP_SETTINGS } from 'angular-engine/modules/ng-express-engine/tokens';

@Injectable()
export class RecaptchaLoaderService {
    private loading: Promise<void>;
    private loaded = false;
    private scriptEl: HTMLScriptElement | null = null;
    private siteKey: string;
    private beforeUnloadHandler: (() => void) | null = null;
    private navigationSub: Subscription;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject(APP_SETTINGS) private settings: any,
        private router: Router
    ) {
        this.registerNavigationUnload();
    }

    load(): Promise<void> {
        if (!isPlatformBrowser(this.platformId)) {
            return Promise.resolve();
        }
        if (this.loaded) {
            return Promise.resolve();
        }
        if (this.loading) {
            return this.loading;
        }

        const key = this.getSiteKey();
        if (!key) {
            return Promise.resolve();
        }
        this.siteKey = key;
        this.registerBeforeUnload();

        this.loading = new Promise((resolve) => {
            if ((window as any).grecaptcha) {
                this.loaded = true;
                resolve();
                return;
            }

            this.scriptEl = document.createElement('script');
            this.scriptEl.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(key);
            this.scriptEl.async = true;
            this.scriptEl.onload = () => {
                this.loaded = true;
                resolve();
            };
            this.scriptEl.onerror = () => {
                this.scriptEl = null;
                this.loading = undefined;
                resolve();
            };
            document.head.appendChild(this.scriptEl);
        });

        return this.loading;
    }

    execute(action: string): Promise<string> {
        if (!isPlatformBrowser(this.platformId)) {
            return Promise.resolve('');
        }
        const key = this.siteKey || this.getSiteKey();
        if (!key) {
            return Promise.resolve('');
        }

        return this.load().then(() => {
            const grecaptcha = (window as any).grecaptcha;
            if (!grecaptcha || !grecaptcha.execute) {
                return '';
            }
            return new Promise<string>((resolve) => {
                grecaptcha.ready(() => {
                    grecaptcha.execute(key, { action })
                        .then((token: string) => resolve(token || ''))
                        .catch(() => resolve(''));
                });
            });
        });
    }

    unload(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.unregisterBeforeUnload();
        const scriptSrcMatch = /recaptcha\/api\.js/;
        if (this.scriptEl && this.scriptEl.parentNode) {
            this.scriptEl.parentNode.removeChild(this.scriptEl);
        }
        Array.prototype.slice.call(document.querySelectorAll('script')).forEach((el: HTMLScriptElement) => {
            if (el.src && scriptSrcMatch.test(el.src)) {
                el.parentNode && el.parentNode.removeChild(el);
            }
        });
        Array.prototype.slice.call(document.querySelectorAll('iframe')).forEach((iframe: HTMLIFrameElement) => {
            if (iframe.src && iframe.src.indexOf('recaptcha') > -1) {
                iframe.parentNode && iframe.parentNode.removeChild(iframe);
            }
        });
        const badge = document.querySelector('.grecaptcha-badge');
        if (badge && badge.parentNode) {
            badge.parentNode.removeChild(badge);
        }
        this.scriptEl = null;
        this.loaded = false;
        this.loading = undefined;
        // best-effort cleanup of global object
        delete (window as any).grecaptcha;
        delete (window as any).___grecaptcha_cfg;
    }

    private registerBeforeUnload() {
        if (this.beforeUnloadHandler || !isPlatformBrowser(this.platformId)) {
            return;
        }
        this.beforeUnloadHandler = () => this.unload();
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    private unregisterBeforeUnload() {
        if (this.beforeUnloadHandler && isPlatformBrowser(this.platformId)) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        }
        this.beforeUnloadHandler = null;
    }

    private registerNavigationUnload() {
        if (this.navigationSub || !isPlatformBrowser(this.platformId)) {
            return;
        }
        this.navigationSub = this.router.events
            .filter(e => e instanceof NavigationStart)
            .subscribe(() => this.unload());
    }

    private getSiteKey(): string {
        const dynamicKey = (window as any)._dynamic_config && (window as any)._dynamic_config.recaptchaSiteKey;
        const settingsKey = this.settings && this.settings['RECAPTCHA_SITE_KEY'];
        return dynamicKey || settingsKey || '';
    }
}



// WEBPACK FOOTER //
// ./src/angular-framework-client-components/components/services/recaptcha-loader.service.ts